import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  TablePagination,
  CircularProgress,
  Button,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import { styled, createTheme, ThemeProvider } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import {
  GetApp as DownloadIcon,
  Search as SearchIcon,
  CalendarToday as CalIcon,
  FilterAltOff as ClearIcon,
} from "@mui/icons-material";
import {
  PurchaseOrdersProvider,
  usePurchaseOrders,
} from "../../contexts/alfaroojfresh/PurchaseOrdersContext";
import * as XLSX from "xlsx";
import STORE_NAMES, { getStoreFromUrl } from "../../utils/STORE_NAMES";
// ─────────────────────────────────────────
// Theme  (matches PurchaseOrders light-mode)
// ─────────────────────────────────────────
const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#f8fafc", paper: "#ffffff" },
    primary: { main: "#3753b3" },
    text: { primary: "#0f172a", secondary: "#64748b" },
  },
  typography: {},
});
// ─────────────────────────────────────────
// Animations
// ─────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;
// ─────────────────────────────────────────
// Styled components  (same palette as PurchaseOrders)
// ─────────────────────────────────────────
const Root = styled("div")({
  minHeight: "100vh",
  background: "#f8fafc",
  padding: "40px 48px",
  animation: `${fadeUp} 500ms ease both`,
});
const TitleBlock = styled("div")({
  "& .eyebrow": {
    fontSize: "0.65rem",
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    color: "#3753b3",
    marginBottom: 6,
  },
  "& h1": {
    margin: 0,
    fontSize: "2rem",
    fontWeight: 700,
    color: "#0f172a",
    letterSpacing: "-0.03em",
    lineHeight: 1,
  },
  "& .sub": { fontSize: "0.75rem", color: "#94a3b8", marginTop: 6 },
});
const ActionBtn = styled(Button)({
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  borderRadius: 8,
  padding: "8px 16px",
  border: "1px solid #e2e8f0",
  color: "#64748b",
  background: "#ffffff",
  transition: "all 0.2s",
  "&:hover": {
    borderColor: "#3753b3",
    color: "#3753b3",
    background: "rgba(245,158,11,0.05)",
  },
});
const StatCard = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: "14px 18px",
  minWidth: 110,
  "& .val": {
    fontSize: "1.4rem",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1,
  },
  "& .lbl": {
    fontSize: "0.62rem",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "#94a3b8",
    marginTop: 4,
  },
});
const FilterBar = styled("div")({
  display: "flex",
  gap: 12,
  marginBottom: 20,
  alignItems: "center",
  flexWrap: "wrap",
});
const SectionBox = styled("div")({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: "24px 28px",
  marginBottom: 20,
  boxShadow: "0 2px 12px rgba(15,23,42,0.04)",
});
const SectionTitle = styled("div")({
  fontSize: "0.85rem",
  fontWeight: 700,
  color: "#0f172a",
  marginBottom: 16,
  display: "flex",
  alignItems: "center",
  gap: 8,
  "& .accent": { color: "#3753b3" },
});
const StyledTableContainer = styled(Paper)({
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
  overflow: "hidden",
});
const tableHeadSx = {
  "& .MuiTableCell-head": {
    fontSize: "0.65rem",
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: "#94a3b8",
    background: "#f8fafc",
    fontWeight: 600,
    padding: "14px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};
const tableBodySx = {
  "& .MuiTableRow-root": {
    transition: "background 0.15s",
    "&:hover": { background: "#fefce8" },
  },
  "& .MuiTableCell-body": {
    fontSize: "0.78rem",
    color: "#475569",
    padding: "12px 16px",
    borderBottom: "1px solid #f1f5f9",
  },
};
const RefChip = styled("span")({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 5,
  background: "#3753b3",
  border: "1px solid #09194d",
  color: "whiteSmoke",
  fontSize: "0.72rem",
  letterSpacing: "0.05em",
});
const inputSx = {
  "& .MuiOutlinedInput-root": {
    fontSize: "0.78rem",
    background: "#ffffff",
    borderRadius: 2,
    color: "#0f172a",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#3753b3" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.75rem", color: "#94a3b8" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#3753b3" },
};
const menuPropsSx = {
  PaperProps: {
    sx: {
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 2,
      boxShadow: "0 8px 24px rgba(15,23,42,0.08)",
      "& .MuiMenuItem-root": {
        fontSize: "0.78rem",
        color: "#475569",
        "&:hover": { background: "#fefce8", color: "#d97706" },
        "&.Mui-selected": {
          background: "rgba(245,158,11,0.08)",
          color: "#3753b3",
        },
      },
    },
  },
};
// ─────────────────────────────────────────
// Helper - compute aggregated line for a good inside a PO
// ─────────────────────────────────────────
function buildGoodLineFromPO(
  po,
  goodId,
  goodsList,
  suppliersList,
  categoriesList
) {
  if (!po || !Array.isArray(po.po_inv_items)) return null;
  const entries = po.po_inv_items.filter(
    (it) => String(it.good) === String(goodId)
  );
  if (!entries.length) return null;
  const qty = entries.reduce((s, e) => s + (Number(e.quantity) || 0), 0);
  const sumPriceQty = entries.reduce(
    (s, e) => s + Number(e.price_used || 0) * (Number(e.quantity) || 0),
    0
  );
  const subtotal = sumPriceQty;
  const taxFromEntries = entries.reduce(
    (s, e) =>
      s +
      (Number(e.total_tax_used) ||
        Number(e.tax_used || 0) * (Number(e.quantity) || 0)),
    0
  );
  const goodObj = goodsList?.find((g) => String(g._id) === String(goodId));
  const goodSupplier = goodObj?.goods_suppliers?.[0] || {};
  const taxRate = Number(goodSupplier?.tax_rate || 0);
  const tax = taxFromEntries > 0 ? taxFromEntries : subtotal * (taxRate / 100);
  const unitPrice = qty
    ? sumPriceQty / qty
    : entries[0]?.price_used || Number(goodSupplier?.price) || 0;
  const totalWithTax = subtotal + tax;
  const supplierId = entries[0]?.supplier || null;
  const supplier = suppliersList?.find(
    (s) => String(s._id) === String(supplierId)
  );
  const categoryId = goodObj?.category;
  const category = categoriesList?.find(
    (c) => String(c._id) === String(categoryId)
  );
  const categoryName = category?.name || "N/A";
  return {
    poRef:
      po.order_no ||
      po.invoice_no ||
      (po._id ? `#${String(po._id).slice(0, 8)}` : "N/A"),
    poId: po._id,
    date: po.received_date || po.orderDate || null,
    supplierId,
    supplierName: supplier?.name || supplierId || "N/A",
    unitPrice,
    qty,
    measurement_unit:
      goodObj?.measurement_unit || goodSupplier?.measurement_unit || "N/A",
    subtotal,
    tax,
    totalWithTax,
    categoryName,
    goodId,
    goodName: goodObj?.name || "N/A",
    rawPo: po,
  };
}
const endpoint0 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_0;
const endpoint1 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_1;
const endpoint2 = process.env.REACT_APP_PURCHASE_ORDERS_ENDPOINT_2;
const baseUrl = process.env.REACT_APP_BASE_URL;
// ─────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────
const PurchasesByGood = () => {
  const {
    purchaseOrders = [],
    purchaseOrders1 = [],
    purchaseOrders2 = [],
    purchaseOrders3 = [],
    purchaseOrders4 = [],
    purchaseOrders5 = [],
    purchaseOrders6 = [],
    purchaseOrders7 = [],
    purchaseOrders8 = [],
    purchaseOrders9 = [],
    purchaseOrders10 = [],
    purchaseOrders11 = [],
    purchaseOrders12 = [],
    purchaseOrders13 = [],
    goods = [],
    suppliers = [],
    categories = [],
    loading,
    error,
  } = usePurchaseOrders();
  // UI state
  const [selectedGood, setSelectedGood] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedStore, setSelectedStore] = useState("");
  // combine all stores' POs into single array
  const allPOs = useMemo(
    () => [
      ...(purchaseOrders || []),
      ...(purchaseOrders1 || []),
      ...(purchaseOrders2 || []),
      ...(purchaseOrders3 || []),
      ...(purchaseOrders4 || []),
      ...(purchaseOrders5 || []),
      ...(purchaseOrders6 || []),
      ...(purchaseOrders7 || []),
      ...(purchaseOrders8 || []),
      ...(purchaseOrders9 || []),
      ...(purchaseOrders10 || []),
      ...(purchaseOrders11 || []),
      ...(purchaseOrders12 || []),
      ...(purchaseOrders13 || []),
    ],
    [
      purchaseOrders,
      purchaseOrders1,
      purchaseOrders2,
      purchaseOrders3,
      purchaseOrders4,
      purchaseOrders5,
      purchaseOrders6,
      purchaseOrders7,
      purchaseOrders8,
      purchaseOrders9,
      purchaseOrders10,
      purchaseOrders11,
      purchaseOrders12,
      purchaseOrders13,
    ]
  );
  const getStoreNameForPO = (po) => {
    const names = [
      "Al Farooj Fresh - Abu Hail Dubai",
      "Al Farooj Fresh - Warehouse",
      "Al Farooj Fresh - Central Kitchen",
      "Al Farooj Fresh - Al Barsha Mall Dubai",
      "Al Farooj Fresh - King Faisal Sharjah",
      "Al Farooj Fresh - Manaseer Al Ain",
      "Al Farooj Fresh - Tawam Al Ain",
      "AL Farooj Fresh - Mirdiff Way Food Truck",
      "Al Farooj Fresh - LIWA",
      "Al Farooj Fresh - Catering ( Al Wasl Club )",
      "Al Farooj Fresh - Ajman",
      "Al Farooj Fresh - Kiosk",
      "Al Farooj Fresh - Al Hilli Al Ain",
      "Al Farooj Fresh - Ajman Food Truck",
    ];
    return names[po.storeIndex] || "Unknown Store";
  };
  // collect unique good IDs from all POs or from selected store's POs
  const uniqueGoodIds = useMemo(() => {
    const ids = new Set();
    const posToCheck = selectedStore
      ? allPOs.filter((po) => getStoreNameForPO(po) === selectedStore)
      : allPOs;
    posToCheck.forEach((po) => {
      if (po.po_inv_items) {
        po.po_inv_items.forEach((item) => {
          if (item.good) ids.add(String(item.good));
        });
      }
    });
    return ids;
  }, [allPOs, selectedStore]);
  const availableGoods = useMemo(() => {
    let filtered = goods.filter((g) => uniqueGoodIds.has(String(g._id)));
    if (selectedSupplier) {
      filtered = filtered.filter((g) =>
        g.goods_suppliers?.some(
          (gs) => String(gs.supplier) === String(selectedSupplier)
        )
      );
    }
    if (selectedCategory) {
      filtered = filtered.filter(
        (g) => String(g.category) === String(selectedCategory)
      );
    }
    return filtered;
  }, [goods, uniqueGoodIds, selectedSupplier, selectedCategory, selectedStore]);
  const availableCategories = useMemo(() => {
    if (!selectedSupplier) return categories;
    const supplierGoods = goods.filter((g) =>
      g.goods_suppliers?.some(
        (gs) => String(gs.supplier) === String(selectedSupplier)
      )
    );
    const catIds = new Set(supplierGoods.map((g) => g.category));
    return categories.filter((c) => catIds.has(c._id));
  }, [categories, goods, selectedSupplier]);
  const availableStores = useMemo(() => {
    const storeNames = new Set();
    allPOs.forEach((po) => {
      storeNames.add(getStoreNameForPO(po));
    });
    return Array.from(storeNames).sort();
  }, [allPOs]);
  const [supplierFilter, setSupplierFilter] = useState("");
  const [storeFilter, setStoreFilter] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // compute rows
  const rows = useMemo(() => {
    if (
      !selectedGood &&
      !selectedCategory &&
      !selectedSupplier &&
      !selectedStore
    )
      return [];
    let allLines = [];
    if (selectedGood) {
      const matchedPOs = allPOs.filter((po) =>
        po.po_inv_items.some(
          (item) => String(item.good) === String(selectedGood)
        )
      );
      matchedPOs.forEach((po) => {
        const line = buildGoodLineFromPO(
          po,
          selectedGood,
          goods,
          suppliers,
          categories
        );
        if (line) {
          line.storeName = getStoreNameForPO(po);
          allLines.push(line);
        }
      });
    } else if (selectedCategory) {
      const matchedPOs = allPOs.filter((po) =>
        po.po_inv_items.some((item) => {
          const good = goods.find((g) => String(g._id) === String(item.good));
          return good && String(good.category) === String(selectedCategory);
        })
      );
      matchedPOs.forEach((po) => {
        const goodIdsInPO = po.po_inv_items
          .map((item) => String(item.good))
          .filter((gid, index, arr) => arr.indexOf(gid) === index);
        goodIdsInPO.forEach((goodId) => {
          const goodObj = goods.find((g) => String(g._id) === goodId);
          if (
            goodObj &&
            String(goodObj.category) === String(selectedCategory)
          ) {
            const line = buildGoodLineFromPO(
              po,
              goodId,
              goods,
              suppliers,
              categories
            );
            if (line) {
              line.storeName = getStoreNameForPO(po);
              allLines.push(line);
            }
          }
        });
      });
    } else if (selectedSupplier) {
      const matchedPOs = allPOs.filter((po) =>
        po.po_inv_items.some(
          (item) => String(item.supplier) === String(selectedSupplier)
        )
      );
      matchedPOs.forEach((po) => {
        const goodIdsInPO = po.po_inv_items
          .filter((item) => String(item.supplier) === String(selectedSupplier))
          .map((item) => String(item.good))
          .filter((gid, index, arr) => arr.indexOf(gid) === index);
        goodIdsInPO.forEach((goodId) => {
          const line = buildGoodLineFromPO(
            po,
            goodId,
            goods,
            suppliers,
            categories
          );
          if (line) {
            line.storeName = getStoreNameForPO(po);
            allLines.push(line);
          }
        });
      });
    } else if (selectedStore) {
      const matchedPOs = allPOs.filter(
        (po) => getStoreNameForPO(po) === selectedStore
      );
      matchedPOs.forEach((po) => {
        const goodIdsInPO = po.po_inv_items
          .map((item) => String(item.good))
          .filter((gid, index, arr) => arr.indexOf(gid) === index);
        goodIdsInPO.forEach((goodId) => {
          const line = buildGoodLineFromPO(
            po,
            goodId,
            goods,
            suppliers,
            categories
          );
          if (line) {
            line.storeName = getStoreNameForPO(po);
            allLines.push(line);
          }
        });
      });
    }
    // apply filters
    const bySupplier = supplierFilter
      ? allLines.filter((r) => String(r.supplierId) === String(supplierFilter))
      : allLines;
    const byStore = storeFilter
      ? bySupplier.filter((r) => r.storeName === storeFilter)
      : bySupplier;
    const fromDate = filterFrom ? new Date(filterFrom) : null;
    const toDate = filterTo ? new Date(filterTo) : null;
    const byDate = byStore.filter((r) => {
      if (!r.date) return true;
      const d = new Date(r.date);
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    });
    const q = (search || "").trim().toLowerCase();
    const bySearch = q
      ? byDate.filter(
          (r) =>
            (r.poRef || "").toString().toLowerCase().includes(q) ||
            (r.supplierName || "").toString().toLowerCase().includes(q) ||
            (r.storeName || "").toString().toLowerCase().includes(q)
        )
      : byDate;
    bySearch.sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
    return bySearch;
  }, [
    selectedGood,
    selectedCategory,
    selectedSupplier,
    selectedStore,
    allPOs,
    goods,
    suppliers,
    categories,
    supplierFilter,
    storeFilter,
    filterFrom,
    filterTo,
    search,
  ]);
  // footer totals
  const footerTotals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.qty += Number(r.qty || 0);
        acc.subtotal += Number(r.subtotal || 0);
        acc.tax += Number(r.tax || 0);
        acc.total += Number(r.totalWithTax || 0);
        return acc;
      },
      { qty: 0, subtotal: 0, tax: 0, total: 0 }
    );
  }, [rows]);
  // pagination slice
  const pageRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  // Has any filter active?
  const hasFilters = filterFrom || filterTo || search;
  // export to excel
  const exportToExcel = () => {
    if (!selectedStore) return;
    let sheetRows = rows.map((r) => ({
      "PO Ref": r.poRef,
      Date: r.date,
      Store: r.storeName,
      Supplier: r.supplierName,
      Good: r.goodName,
      Category: r.categoryName,
      "Unit Price": Number(r.unitPrice || 0),
      Quantity: Number(r.qty || 0),
      Unit: r.measurement_unit,
      Subtotal: Number(r.subtotal || 0),
      Tax: Number(r.tax || 0),
      Total: Number(r.totalWithTax || 0),
    }));
    // Consolidate by Good
    const aggregatedMap = {};
    sheetRows.forEach((r) => {
      const good = r["Good"];
      if (!aggregatedMap[good]) {
        aggregatedMap[good] = { ...r, "PO Ref": "Consolidated" };
      } else {
        aggregatedMap[good]["Quantity"] += r["Quantity"];
        aggregatedMap[good]["Subtotal"] += r["Subtotal"];
        aggregatedMap[good]["Tax"] += r["Tax"];
        aggregatedMap[good]["Total"] += r["Total"];
      }
    });
    sheetRows = Object.values(aggregatedMap);
    // Add totals row
    const totalsRow = {
      "PO Ref": "Totals",
      Date: "",
      Store: "",
      Supplier: "",
      Good: "",
      Category: "",
      "Unit Price": "",
      Quantity: Number(footerTotals.qty || 0),
      Unit: "",
      Subtotal: Number(footerTotals.subtotal || 0),
      Tax: Number(footerTotals.tax || 0),
      Total: Number(footerTotals.total || 0),
    };
    sheetRows.push(totalsRow);
    const ws = XLSX.utils.json_to_sheet(sheetRows);
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const numberColumns = [
      "Unit Price",
      "Quantity",
      "Subtotal",
      "Tax",
      "Total",
    ];
    for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        const headerRef = XLSX.utils.encode_cell({ r: 0, c: C });
        const header = ws[headerRef]?.v;
        if (numberColumns.includes(header)) {
          const cell = ws[cellRef];
          if (cell && typeof cell.v === "number") {
            cell.t = "n";
            cell.z = "#,##0.00";
          }
        }
      }
    }
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell_address = { c: C, r: range.e.r };
      const cell_ref = XLSX.utils.encode_cell(cell_address);
      if (!ws[cell_ref]) ws[cell_ref] = { t: "s", v: "" };
      ws[cell_ref].s = { font: { bold: true } };
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Good Purchases");
    const name = selectedGood
      ? goods.find((g) => String(g._id) === String(selectedGood))?.name ||
        "Good"
      : selectedCategory
      ? categories.find((c) => String(c._id) === String(selectedCategory))
          ?.name || "Category"
      : suppliers.find((s) => String(s._id) === String(selectedSupplier))
          ?.name || "Supplier";
    XLSX.writeFile(
      wb,
      `Purchases_${name}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };
  // ── Loading / Error states ──
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ background: "#f8fafc" }}
      >
        <CircularProgress sx={{ color: "#3753b3" }} />
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ background: "#f8fafc" }}
      >
        <Typography sx={{ color: "#ef4444" }}>Error: {String(error)}</Typography>
      </Box>
    );
  }
  return (
    <ThemeProvider theme={theme}>
      <Root>
        {/* ── Page header ── */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={3}
          flexWrap="wrap"
          gap={2}
        >
          <TitleBlock>
            <div className="eyebrow">Procurement</div>
            <h1>Purchases by Good</h1>
            <div className="sub">
              Select a store, supplier, category, or good to list every PO where
              that good has been purchased (aggregated per PO).
            </div>
          </TitleBlock>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <StatCard>
              <div className="val">{rows.length}</div>
              <div className="lbl">Results</div>
            </StatCard>
            <StatCard>
              <div className="val">AED {footerTotals.total.toFixed(0)}</div>
              <div className="lbl">Total Value</div>
            </StatCard>
            <ActionBtn
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={!selectedStore || rows.length === 0}
            >
              Excel
            </ActionBtn>
          </Box>
        </Box>
        {/* ── Selection Filters ── */}
        <SectionBox>
          <SectionTitle>
            <span className="accent">◈</span> Selection Filters
          </SectionTitle>
          <FilterBar>
            <Autocomplete
              options={availableStores}
              getOptionLabel={(option) => option}
              value={selectedStore}
              onChange={(event, newValue) => {
                setSelectedStore(newValue || "");
                setSelectedSupplier("");
                setSelectedCategory("");
                setSelectedGood("");
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Store"
                  size="small"
                  sx={{ ...inputSx, minWidth: 280 }}
                />
              )}
              size="small"
              sx={{ minWidth: 280 }}
            />
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) =>
                option._id === value._id
              }
              value={
                suppliers.find((s) => s._id === selectedSupplier) || null
              }
              onChange={(event, newValue) => {
                setSelectedSupplier(newValue ? newValue._id : "");
                setSelectedCategory("");
                setSelectedGood("");
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier"
                  size="small"
                  sx={{ ...inputSx, minWidth: 280 }}
                />
              )}
              size="small"
              sx={{ minWidth: 280 }}
            />
            <Autocomplete
              options={availableCategories}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) =>
                option._id === value._id
              }
              value={
                availableCategories.find(
                  (c) => c._id === selectedCategory
                ) || null
              }
              onChange={(event, newValue) => {
                setSelectedCategory(newValue ? newValue._id : "");
                setSelectedGood("");
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  size="small"
                  sx={{ ...inputSx, minWidth: 280 }}
                />
              )}
              size="small"
              sx={{ minWidth: 280 }}
            />
            <Autocomplete
              options={availableGoods}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) =>
                option._id === value._id
              }
              value={
                availableGoods.find((g) => g._id === selectedGood) || null
              }
              onChange={(event, newValue) => {
                setSelectedGood(newValue ? newValue._id : "");
                setPage(0);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Good"
                  size="small"
                  sx={{ ...inputSx, minWidth: 280 }}
                />
              )}
              size="small"
              sx={{ minWidth: 280 }}
            />
          </FilterBar>
        </SectionBox>
        {/* ── Refine Filters ── */}
        <SectionBox>
          <SectionTitle>
            <span className="accent">◈</span> Refine Results
          </SectionTitle>
          <FilterBar>
            <TextField
              placeholder="Search…"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#94a3b8", fontSize: 16 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, flex: 1, minWidth: 220 }}
            />
            <TextField
              label="From"
              variant="outlined"
              size="small"
              type="date"
              value={filterFrom}
              onChange={(e) => {
                setFilterFrom(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, minWidth: 160 }}
            />
            <TextField
              label="To"
              variant="outlined"
              size="small"
              type="date"
              value={filterTo}
              onChange={(e) => {
                setFilterTo(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalIcon sx={{ color: "#94a3b8", fontSize: 14 }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, minWidth: 160 }}
            />
            {hasFilters && (
              <ActionBtn
                startIcon={<ClearIcon sx={{ fontSize: 15 }} />}
                onClick={() => {
                  setSearch("");
                  setFilterFrom("");
                  setFilterTo("");
                }}
                sx={{
                  borderColor: "#fecaca",
                  color: "#ef4444",
                  "&:hover": {
                    borderColor: "#ef4444",
                    background: "rgba(239,68,68,0.05)",
                  },
                }}
              >
                Clear
              </ActionBtn>
            )}
          </FilterBar>
        </SectionBox>
        {/* ── Data Table ── */}
        <SectionBox>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            flexWrap="wrap"
            gap={1}
          >
            <SectionTitle sx={{ mb: 0 }}>
              <span className="accent">◈</span> Purchase Orders
              <span
                style={{
                  color: "#94a3b8",
                  fontWeight: 400,
                  fontSize: "0.72rem",
                }}
              >
                — {rows.length} records
              </span>
            </SectionTitle>
            <Box display="flex" gap={3}>
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Total Qty:{" "}
                <strong style={{ color: "#0f172a" }}>
                  {footerTotals.qty?.toFixed(4)}
                </strong>
              </span>
              <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                Total Value:{" "}
                <strong style={{ color: "#3753b3" }}>
                  AED {footerTotals.total.toFixed(4)}
                </strong>
              </span>
            </Box>
          </Box>
          <TableContainer component={StyledTableContainer}>
            <Table sx={{ ...tableHeadSx, ...tableBodySx }}>
              <TableHead>
                <TableRow>
                  <TableCell>PO Ref</TableCell>
                  <TableCell>Date</TableCell>
                  {/* <TableCell>Store</TableCell> */}
                  <TableCell>Supplier</TableCell>
                  <TableCell>Good</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((r) => (
                  <TableRow key={`${r.poId}-${r.goodId}`}>
                    <TableCell>
                      <RefChip>
                        {r.poRef}
                      </RefChip>
                      {r.rawPo?.order_no ? (
                        <div
                          style={{
                            fontSize: "0.65rem",
                            color: "#94a3b8",
                            marginTop: 2,
                          }}
                        >
                          {r.rawPo.order_no}
                        </div>
                      ) : null}
                    </TableCell>
                    <TableCell>{r.date || "—"}</TableCell>
                    {/* <TableCell>
                      <span style={{ color: "#0f172a", fontWeight: 600 }}>
                        {getStoreNameForPO(r.rawPo)}
                      </span>
                    </TableCell> */}
                    <TableCell>
                      <span style={{ color: "#0f172a", fontWeight: 600 }}>
                        {r.supplierName}
                      </span>
                    </TableCell>
                    <TableCell>{r.goodName}</TableCell>
                    <TableCell align="right">
                      {Number(r.unitPrice || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">{Number(r.qty || 0)}</TableCell>
                    <TableCell>
                      <span
                        style={{
                          color: "#cbd5e1",
                          fontSize: "0.7rem",
                        }}
                      >
                        {r.measurement_unit}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      <span style={{ color: "#3753b3", fontWeight: 600 }}>
                        {Number(r.subtotal || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell align="right">
                      {Number(r.tax || 0).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      <span style={{ fontWeight: 700, color: "#0f172a" }}>
                        {Number(r.totalWithTax || 0).toFixed(2)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span style={{ color: "#94a3b8" }}>
                        {r.categoryName}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
                {pageRows.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      align="center"
                      sx={{
                        color: "#94a3b8 !important",
                        padding: "36px !important",
                        fontStyle: "italic",
                      }}
                    >
                      {!selectedGood &&
                      !selectedCategory &&
                      !selectedSupplier &&
                      !selectedStore
                        ? "Select a store, supplier, category, or good to start."
                        : "No purchase orders found with current filters."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ background: "#f8fafc" }}>
                  <TableCell
                    colSpan={6}
                    sx={{
                      fontWeight: 700,
                      textAlign: "right",
                      color: "#0f172a !important",
                      fontSize: "0.75rem !important",
                    }}
                  >
                    Totals:
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {footerTotals.qty}
                  </TableCell>
                  <TableCell />
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: "#3753b3 !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {footerTotals.subtotal.toFixed(2)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {footerTotals.tax.toFixed(2)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 700,
                      color: "#0f172a !important",
                      fontSize: "0.78rem !important",
                    }}
                  >
                    {footerTotals.total.toFixed(2)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={rows.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{
              color: "#94a3b8",
              fontSize: "0.72rem",
              "& .MuiSelect-icon": { color: "#94a3b8" },
              "& .MuiIconButton-root": {
                color: "#94a3b8",
                "&:hover": { color: "#3753b3" },
              },
              "& .Mui-disabled": { color: "#e2e8f0 !important" },
            }}
          />
        </SectionBox>
      </Root>
    </ThemeProvider>
  );
};
// Wrap with provider
const PurchasesByGoodPage = () => (
  <PurchaseOrdersProvider
    endpoint0={`${baseUrl}/api/api-farooj-index/combined-0`}
    endpoint1={`${baseUrl}/api/api-farooj-index/combined-1`}
    endpoint2={`${baseUrl}/api/api-farooj-index/combined-2`}
    endpoint3={`${baseUrl}/api/api-farooj-index/combined-3`}
    endpoint4={`${baseUrl}/api/api-farooj-index/combined-4`}
    endpoint5={`${baseUrl}/api/api-farooj-index/combined-5`}
    endpoint6={`${baseUrl}/api/api-farooj-index/combined-6`}
    endpoint7={`${baseUrl}/api/api-farooj-index/combined-7`}
    endpoint8={`${baseUrl}/api/api-farooj-index/combined-8`}
    endpoint9={`${baseUrl}/api/api-farooj-index/combined-9`}
    endpoint10={`${baseUrl}/api/api-farooj-index/combined-10`}
    endpoint11={`${baseUrl}/api/api-farooj-index/combined-11`}
    endpoint12={`${baseUrl}/api/api-farooj-index/combined-12`}
    endpoint13={`${baseUrl}/api/api-farooj-index/combined-13`}
  >
    <PurchasesByGood />
  </PurchaseOrdersProvider>
);
export default PurchasesByGoodPage;